import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Box, Flex, ColorVariant, Tabs as PillTab, TabsContainer, TablesGrid } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { IDashboardTabProps } from 'types/foodLogistics/foodTabProps.interface'
import { IFoodKitchen } from 'types/foodLogistics/foodKitchen.interface'
import { FOOD_STATUS_COLORS } from '../foodData'

export const DashboardTab = ({
  windows,
  kitchens,
  kitchenOrdered,
  confirmations,
  exceptions,
  openExceptions,
}: IDashboardTabProps) => {
  const navigate = useNavigate()
  const [sumWin, setSumWin] = useState<number | null>(null)
  const openExc = exceptions.filter((e) => e.status === 'Open')
  const critHigh = openExc.filter((e) => e.sev === 'Critical' || e.sev === 'High').length

  useEffect(() => {
    if (!windows.length) {
      setSumWin(null)
      return
    }
    const stillValid = sumWin != null && windows.some((w) => w.id === sumWin)
    if (!stillValid) {
      setSumWin(windows.find((w) => w.locked)?.id ?? windows[0].id)
    }
  }, [windows, sumWin])

  const servedFor = (w: { id: number }) => kitchens.reduce((s, k) => s + (confirmations[`${w.id}-${k.id}`]?.served || 0), 0)
  const allConfirmed = (wId: number) =>
    kitchens.every((k) => (kitchenOrdered[wId]?.[k.id] || 0) === 0 || confirmations[`${wId}-${k.id}`])
  const canFinalize = critHigh === 0 && windows.filter((w) => w.locked).every((w) => allConfirmed(w.id))

  const summaryColumns: IHonestTableColumn<IFoodKitchen>[] = [
    {
      id: 'kitchen',
      header: 'Kitchen',
      sticky: true,
      minWidth: '12rem',
      cell: (k) => (
        <Cell>
          <BodyText weight='600'>{k.name}</BodyText>
        </Cell>
      ),
    },
    {
      id: 'ordered',
      header: 'Ordered',
      cell: (k) => {
        const w = windows.find((x) => x.id === sumWin)
        const ord = kitchenOrdered[sumWin]?.[k.id] || 0
        return w?.locked ? <BodyText>{ord}</BodyText> : <OtherText grey>Locks at cut-off</OtherText>
      },
    },
    {
      id: 'served',
      header: 'Served',
      cell: (k) => {
        const c = confirmations[`${sumWin}-${k.id}`]
        return <BodyText>{c ? c.served : '—'}</BodyText>
      },
    },
    {
      id: 'standbyUsed',
      header: 'Standby Used',
      cell: (k) => {
        const c = confirmations[`${sumWin}-${k.id}`]
        return <BodyText>{c ? c.standbyUsed : '—'}</BodyText>
      },
    },
    {
      id: 'confirmation',
      header: 'Confirmation',
      cell: (k) => {
        const w = windows.find((x) => x.id === sumWin)
        const c = confirmations[`${sumWin}-${k.id}`]
        const ord = kitchenOrdered[sumWin]?.[k.id] || 0
        if (!w?.locked) {
          return (
            <StatusPill status='accent'>Opens after cut-off</StatusPill>
          )
        }
        if (c) {
          return (
            <StatusPill status='success'>{`Confirmed${c.onBehalf ? ' (on behalf)' : ''}`}</StatusPill>
          )
        }
        if (ord > 0) {
          return (
            <StatusPill status='accent'>Awaiting</StatusPill>
          )
        }
        return <OtherText grey>—</OtherText>
      },
    },
    {
      id: 'stock',
      header: 'Standby Stock (per diet)',
      cell: (k) => (
        <Flex wrap gap='0.25rem'>
          {k.standby.map((s) => (
            <StatusPill
              key={s.tags.join()}
              status={s.qty <= s.min ? 'danger' : 'accent'}
            >
              {`${s.tags.join('+')} ×${s.qty}`}
            </StatusPill>
          ))}
        </Flex>
      ),
    },
  ]

  return (
    <Flex column gap='1rem' width='100%'>
      <Alert
        title='Unknown presence — Dinner window: 12 persons'
        description='Active policy: No Meal — arrivals served from standby. Attendance reconciliation runs until Dinner cut-off (15:30).'
        type={AlertType.WARNING}
        colorVariant={ColorVariant.PRIMARY}
      />

      <Flex gap='0.875rem' width='100%'>
        {windows.map((w) => (
          <Box key={w.id} width='100%' hoverOrangeBorder onClick={() => navigate(`/hr/food/window/${w.id}`)}>
            <Flex between center>
              <BodyText weight='700'>{w.name}</BodyText>
              <StatusPill status={w.status === 'Cancelled' ? 'danger' : w.status === 'Served' || w.status === 'Ordered' || w.status === 'Finalized' ? 'success' : w.status === 'Planned' ? 'info' : 'accent'}>{w.status}</StatusPill>
            </Flex>
            <OtherText grey>
              {w.time} · cut-off {w.cutoff}
            </OtherText>
            <Flex gap='1.75rem' mt='12' width='100%'>
              <Flex column>
                <TitleText weight='700'>{w.planned}</TitleText>
                <OtherText grey>Planned</OtherText>
              </Flex>
              <Flex column>
                <TitleText weight='700'>{w.ordered}</TitleText>
                <OtherText grey>Ordered</OtherText>
              </Flex>
              <Flex column>
                <TitleText weight='700'>{servedFor(w)}</TitleText>
                <OtherText grey>Served</OtherText>
              </Flex>
            </Flex>
          </Box>
        ))}
      </Flex>

      <Container>
        <ContainerHeader padding>
          <Flex between center width='100%' wrap gap='0.75rem'>
            <TitleText weight='700'>Kitchen Summary</TitleText>
            <TabsContainer>
              {windows.map((w) => (
                <PillTab key={w.id} active={sumWin === w.id} onClick={() => setSumWin(w.id)}>
                  {w.name}
                </PillTab>
              ))}
            </TabsContainer>
          </Flex>
        </ContainerHeader>
        <ContainerContent>
          <HonestTable columns={summaryColumns} data={kitchens} keyField='id' />
        </ContainerContent>
      </Container>

      <TablesGrid>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Exceptions</TitleText>
          </ContainerHeader>
          <ContainerContent gap='0.75rem'>
            <Flex gap='0.75rem' wrap width='100%'>
              {(['Critical', 'High', 'Medium', 'Low'] as const).map((s) => (
                <Box key={s} width='100%' style={{ minWidth: '11rem', flex: '1 1 11rem' }}>
                  <MetricCard
                    value={openExc.filter((e) => e.sev === s).length}
                    label={s}
                    accentColor={FOOD_STATUS_COLORS[s]}
                  />
                </Box>
              ))}
            </Flex>
            <Button variant='secondary' onClick={openExceptions}>
              Open Exceptions
            </Button>
          </ContainerContent>
        </Container>

        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Operational Day Close</TitleText>
          </ContainerHeader>
          <ContainerContent gap='0.75rem'>
            {!canFinalize ? (
              <Alert
                title='Auto-finalize blocked'
                description={`${!windows
                  .filter((w) => w.locked)
                  .every((w) => kitchens.every((k) => (kitchenOrdered[w.id]?.[k.id] || 0) === 0 || confirmations[`${w.id}-${k.id}`]))
                  ? 'Kitchen confirmation(s) missing. '
                  : ''
                  }${critHigh > 0 ? `${critHigh} open Critical/High exception(s).` : ''}`}
                type={AlertType.WARNING}
                colorVariant={ColorVariant.PRIMARY}
              />
            ) : (
              <Alert
                title='Ready to finalize'
                description='All conditions met — day will auto-finalize at close.'
                type={AlertType.SUCCESS}
                colorVariant={ColorVariant.GREEN}
              />
            )}
            <Button variant='primary' disabled={!canFinalize}>
              Finalize Now
            </Button>
          </ContainerContent>
        </Container>
      </TablesGrid>
    </Flex>
  )
}
